import { useCallback, useMemo } from 'react';

import { useGetCountries } from '@/_containers/inputs/address/hooks/use-get-countries-query';
import { sanitizeCountry } from '@/_schemas/countries';
import type { PhoneNumberQuerySchema } from '@/_schemas/phone-numbers';
import { sanitizePhoneNumber } from '@/_schemas/phone-numbers';

import { stringToType, useFormDefinition, useUpdateMutation } from '../../shared';
import { type PhoneNumberProps } from '../../types';

export type Props = Omit<PhoneNumberProps, 'value'> & {
  index: number;
  value: PhoneNumberQuerySchema[];
  validateDuplicatePhoneNumber?: (phoneNumber: string) => Promise<boolean>;
};

export function useUpdateNumber({ onChange, index, value, validateDuplicatePhoneNumber }: Props) {
  const { data: cData } = useGetCountries();
  const numberObj = value[index];
  const { id, type: strType } = value[index];
  const { isPending, mutateAsync } = useUpdateMutation(`${id}`);

  const { countries, defaultCountryValue } = useMemo(() => {
    const rawCountries = cData?.data ?? [];

    const countryValue = (rawCountries.find((item) => item.id === numberObj.country.id) ??
      {}) as PhoneNumberQuerySchema['country'];

    return { countries: rawCountries, defaultCountryValue: sanitizeCountry(countryValue) };
  }, [cData?.data, numberObj.country.id]);

  const form = useFormDefinition({
    ...numberObj,
    number: sanitizePhoneNumber(numberObj),
    country: sanitizeCountry(numberObj.country),
    type: stringToType(strType),
    isDuplicate: false,
  });

  const [number] = form.watch(['number']);

  const onSubmit = useCallback(async () => {
    const res = await form.trigger();
    if (!res) {
      // Return undefined to differentiate between API error and form validation error
      return undefined;
    }

    const { type, country, ...all } = form.getValues();

    try {
      const response = await mutateAsync({
        ...all,
        country_id: country.id,
        type: type.value,
      });

      if (response) {
        const nv = value.map((item) => {
          if (item.id === id) {
            return { ...response.data, country: { ...response.data.country, ...country } };
          }
          return { ...item };
        });

        onChange('UPDATE')(nv);
        return true;
      }
    } catch (error) {
      console.error(error);
      return false;
    }

    return false;
  }, [form, id, mutateAsync, onChange, value]);

  const onValidateDuplicate = useCallback(async () => {
    if (validateDuplicatePhoneNumber) {
      const isDuplicate = await validateDuplicatePhoneNumber(number);
      form.setValue('isDuplicate', isDuplicate);
      if (isDuplicate) {
        form.setError('number', { message: 'This phone number is already added' });
      }
    }
  }, [form, number, validateDuplicatePhoneNumber]);

  return { id, onSubmit, form, isPending, countries, defaultCountryValue, onValidateDuplicate };
}
